import { useEffect, useRef, useState } from 'react';
import { init } from 'pptx-preview';

interface PptViewerProps {
  url: string;
}

export default function PptViewer({ url }: PptViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<ReturnType<typeof init> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let destroyed = false;

    const loadPpt = async () => {
      try {
        setLoading(true);
        setError(null);

        // 销毁旧预览器
        if (viewerRef.current) {
          viewerRef.current.destroy();
          viewerRef.current = null;
        }
        container.innerHTML = '';

        const response = await fetch(url);
        const arrayBuffer = await response.arrayBuffer();

        if (destroyed) return;

        // 用实际容器尺寸初始化，兜底 960x540
        const viewer = init(container, {
          width: container.clientWidth || 960,
          height: container.clientHeight || 540,
        });
        viewerRef.current = viewer;

        await viewer.preview(arrayBuffer);

        if (!destroyed) setLoading(false);
      } catch (err) {
        if (!destroyed) {
          console.error('PPT 加载失败:', err);
          setError('无法加载 PPT 文件');
          setLoading(false);
        }
      }
    };

    loadPpt();

    return () => {
      destroyed = true;
      if (viewerRef.current) {
        viewerRef.current.destroy();
        viewerRef.current = null;
      }
    };
  }, [url]);

  return (
    <div className="relative w-full h-full bg-gray-100">
      {/* 容器始终渲染，供 pptx-preview 挂载 */}
      <div ref={containerRef} className="w-full h-full overflow-auto flex justify-center" />

      {/* loading / error 叠加层 */}
      {loading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gray-100/90">
          <div className="w-10 h-10 border-3 border-gray-400 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-gray-500">加载 PPT...</span>
        </div>
      )}
      {error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gray-100/90">
          <i className="ri-error-warning-line text-4xl text-amber-500" />
          <span className="text-sm text-gray-500">{error}</span>
        </div>
      )}
    </div>
  );
}
